import * as React from "react";

import {
  useCompletedFormState,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { BOAT_ABOUT_STEP, BOAT_FIND_STEP } from "../boatSteps";

import { Paragraph } from "App/global-style";
import { CheckPriceProps } from "Components/Features/CheckPrice";
import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { DamageRecentYears } from "Components/Form/RecentDamages/recentDamagesInterfaces";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { TextManager } from "Components/TextContext";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { typeWithMotorOrSail } from "Store/interfaces/products/checkout/riskProducts/BoatProduct";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { useProductTexts } from "Store/texts/hooks";
import { formBooleanToBoolean } from "Utils/formUtils";
import { BoatProductIntroFormValues } from "../ProductIntro/boatProductIntroInterfaces";
import { BoatPriceRequestInfo } from "../boatTypes";
import { CommonFormElements } from "./CommonFormElements";
import { CommonFormElementsMotorOrSail } from "./CommonFormElementsMotorOrSail";
import { BoatProductDetailsFormValues } from "./boatProductDetailsInterfaces";
import { validationSchema } from "./boatProductDetailsValidation";

const productName = INSURANCE_NAME_VALUE.BOAT;

export const BoatProductDetails = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = TextManager;
  const [texts, lang] = useProductTexts(productName.toLowerCase());
  const introFormValues = useCompletedFormState<BoatProductIntroFormValues>(
    BOAT_FIND_STEP.id,
  );
  const initialFormState = useFormState<BoatProductDetailsFormValues>(
    BOAT_ABOUT_STEP.id,
    initialFormValues,
  );

  const { isLoading, error } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );

  const handleSubmit = (form: BoatProductDetailsFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        productName: productName,
        stepId: BOAT_ABOUT_STEP.id,
        formState: form,
      }),
    );

    updatePriceRequest<BoatPriceRequestInfo>({
      productPriceUrl: BackendProductInfo.BOAT.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        ...introFormValues,
        numberOfYearsAsOwner: Number(introFormValues.numberOfYearsAsOwner),
        harbourZipCode: form.postNumber,
        hullAndEngineIdMarked: formBooleanToBoolean(form.hullAndEngineIdMarked),
        memberOfRedningsselskapetCoastGuard: formBooleanToBoolean(
          form.memberOfRedningsselskapetCoastGuard,
        ),
        usedByYoungerDriver: formBooleanToBoolean(form.usedByYoungerDriver),
        harbourSecurity: formBooleanToBoolean(form.harbourSecurity),
        trackingSystem: formBooleanToBoolean(form.trackingSystem),
        boatNotPristine: formBooleanToBoolean(introFormValues.boatNotPristine),
        injuries: Number(form.injuries),
        injuryYear: form.injuryYear ? Number(form.injuryYear) : undefined,
      },
    });
  };

  return (
    <ProductDetailsTile
      header={texts.tile.productDetails.heading[lang]}
      initialValues={initialFormState}
      validationSchema={validationSchema(introFormValues.boatType)}
      onSubmit={handleSubmit}
    >
      {(formProps: FormikProps<BoatProductDetailsFormValues>) => (
        <Form>
          <Paragraph>{getText("boatform.ingress.boatdetails")}</Paragraph>

          {typeWithMotorOrSail(introFormValues.boatType) && (
            <CommonFormElementsMotorOrSail formProps={formProps} />
          )}
          <CommonFormElements formProps={formProps} />

          {error && <SystemErrorAlert alertId="priceCalculation" />}
          <ScrollToFirstError />

          <NavigationButtons
            isLoading={isLoading}
            disableNextButton={
              formProps.values.injuries === DamageRecentYears.TWO_OR_MORE
            }
          />
        </Form>
      )}
    </ProductDetailsTile>
  );
};

const initialFormValues = {
  injuries: undefined,
  injuryYear: undefined,
  boatNotPristine: undefined,
  city: undefined,
  harbourSecurity: undefined,
  hullAndEngineIdMarked: undefined,
  memberOfRedningsselskapetCoastGuard: undefined,
  postNumber: undefined,
  trackingSystem: undefined,
  usedByYoungerDriver: undefined,
  validPostNumber: undefined,
};
